import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, RefreshCw, X, Check, GitCompare } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

/**
 * "Rewrite with AI" workflow — non-destructive.
 *
 *   1. Admin sees the ORIGINAL post on top (read-only, rendered HTML).
 *   2. Types refinement instructions ("make it shorter", "more SEO-friendly",
 *      "switch to first-person", etc.) and clicks Generate.
 *   3. AI draft appears underneath — fully formatted (headings, bold, lists,
 *      tables — already converted to HTML server-side).
 *   4. Admin can Re-generate with new instructions, Reject (keeps the
 *      original, closes the dialog), or Edit & Replace (opens the editor
 *      with the AI draft pre-loaded; the original stays in the DB until
 *      the admin presses Save in the editor).
 */
export const RewriteWithAiDialog = ({ post, open, onOpenChange, onAcceptForEdit }) => {
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  // Diff view — when on, the bottom panel renders a word-level diff of
  // original vs AI rewrite (additions in green, removals in red strikethrough)
  // so the admin can spot-check exactly what the AI changed before accepting.
  const [showDiff, setShowDiff] = useState(false);
  const [diffHtml, setDiffHtml] = useState('');

  // Reset everything when the dialog closes so the next post starts clean.
  React.useEffect(() => {
    if (!open) {
      setInstructions('');
      setAiDraft(null);
      setGenerating(false);
      setShowDiff(false);
      setDiffHtml('');
    }
  }, [open]);

  // Build the word-level diff lazily — only when the admin toggles the
  // diff view AND there's both an original + AI draft to compare. We
  // strip HTML tags before diffing so the visual noise is just the words
  // that changed, not span/p tag churn.
  React.useEffect(() => {
    if (!showDiff || !aiDraft || !post) {
      setDiffHtml('');
      return;
    }
    (async () => {
      try {
        const { diffWords } = await import('diff');
        const stripHtml = (s) => {
          const tmp = document.createElement('div');
          tmp.innerHTML = s || '';
          return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
        };
        const a = stripHtml(post.content || '');
        const b = stripHtml(aiDraft.content_html || '');
        const parts = diffWords(a, b);
        const escape = (t) => t
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        const html = parts.map((p) => {
          const txt = escape(p.value);
          if (p.added) return `<ins style="background:#10b98133;color:#065f46;text-decoration:none;padding:1px 2px;border-radius:2px">${txt}</ins>`;
          if (p.removed) return `<del style="background:#ef444433;color:#7f1d1d;text-decoration:line-through;padding:1px 2px;border-radius:2px">${txt}</del>`;
          return txt;
        }).join('');
        setDiffHtml(html);
      } catch (e) {
        setDiffHtml('<p class="text-muted-foreground">Diff failed to compute.</p>');
      }
    })();
  }, [showDiff, aiDraft, post]);

  const handleGenerate = async () => {
    if (instructions.trim().length < 4) {
      toast.error('Please describe how the AI should rewrite this post.');
      return;
    }
    setGenerating(true);
    const { data, error } = await apiFetch('/api/admin/ai-blog/rewrite', {
      method: 'POST',
      body: {
        post_id: post.post_id,
        instructions: instructions.trim(),
      },
    });
    setGenerating(false);
    if (error) {
      toast.error(error);
      return;
    }
    setAiDraft(data);
    toast.success('Rewrite ready — review below.');
  };

  const handleAccept = () => {
    if (!aiDraft) return;
    // Hand the draft off to the editor via sessionStorage (same pipeline
    // the new-post AI generator already uses). The editor will pre-fill
    // with the AI version; the original post stays untouched in the DB
    // until the admin presses Save there.
    sessionStorage.setItem('aiBlogDraft', JSON.stringify({
      ...aiDraft,
      // The editor needs to know we're replacing an EXISTING post, not
      // creating a new one. Carry the post_id so the editor PUTs to the
      // right document on save.
      replace_post_id: post.post_id,
      cover_image: post.cover_image || null,
    }));
    onAcceptForEdit?.(post.post_id);
    onOpenChange(false);
  };

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="rewrite-with-ai-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#00A896]" />
            Rewrite with AI
          </DialogTitle>
          <DialogDescription>
            Your original post stays safe until you press Save in the editor.
            You can re-generate as many times as you like, then Edit & Replace
            or Reject and keep what you have.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 px-1">
          {/* Original — read-only, always visible at the top */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Original post {post.published ? '· Published' : '· Draft'}
              </Label>
              <Badge variant="outline" className="text-[10px]">read-only</Badge>
            </div>
            <div
              className="border rounded-md bg-slate-50 dark:bg-slate-900/40 dark:border-slate-700 p-4 max-h-[260px] overflow-y-auto"
              data-testid="rewrite-original-content"
            >
              <h4 className="font-semibold text-sm mb-2">{post.title}</h4>
              <div
                className="prose prose-sm dark:prose-invert max-w-none
                  [&_h2]:text-base [&_h3]:text-sm
                  [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto"
                dangerouslySetInnerHTML={{ __html: post.content || '<p class="text-muted-foreground">(empty)</p>' }}
              />
            </div>
          </div>

          {/* Instructions */}
          <div>
            <Label htmlFor="rewrite-instructions" className="text-sm font-medium">
              How should the AI rewrite it?
            </Label>
            <Textarea
              id="rewrite-instructions"
              rows={3}
              placeholder='e.g. "Make this 30% shorter and switch to first-person voice" or "Add more SEO-friendly H2s about workplace communication"'
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="mt-1"
              data-testid="rewrite-instructions-input"
            />
            <div className="flex justify-end mt-2">
              <Button
                onClick={handleGenerate}
                disabled={generating || instructions.trim().length < 4}
                className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
                data-testid="rewrite-generate-btn"
              >
                {generating
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Rewriting (15-30s)…</>
                  : aiDraft
                    ? <><RefreshCw className="h-4 w-4 mr-2" /> Generate again</>
                    : <><Sparkles className="h-4 w-4 mr-2" /> Generate rewrite</>}
              </Button>
            </div>
          </div>

          {/* AI draft preview — only after a successful generate */}
          {aiDraft && (
            <div data-testid="rewrite-ai-preview">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#00A896]">
                  AI rewrite — already formatted (drops into editor as-is)
                </Label>
                <div className="flex gap-1 flex-wrap items-center">
                  <Button
                    type="button"
                    size="sm"
                    variant={showDiff ? 'default' : 'outline'}
                    onClick={() => setShowDiff((v) => !v)}
                    className="h-7 text-xs"
                    title="Toggle between rendered AI rewrite and a word-level diff against the original"
                    data-testid="rewrite-toggle-diff-btn"
                  >
                    <GitCompare className="h-3 w-3 mr-1" />
                    {showDiff ? 'Show rewrite' : 'Show diff'}
                  </Button>
                  <Badge variant="outline" className="text-[10px]">{aiDraft.category}</Badge>
                  {(aiDraft.tags || []).slice(0, 3).map((t) => (
                    <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                  ))}
                </div>
              </div>
              <div className="border-2 border-[#00E5CC]/30 rounded-md bg-[#00E5CC]/5 p-4 max-h-[320px] overflow-y-auto">
                <h4 className="font-semibold text-base mb-2">{aiDraft.title}</h4>
                {aiDraft.excerpt && (
                  <p className="text-sm italic text-muted-foreground mb-3 border-l-2 border-[#00E5CC]/40 pl-3">
                    {aiDraft.excerpt}
                  </p>
                )}
                {showDiff ? (
                  <div
                    className="text-sm leading-relaxed whitespace-pre-wrap break-words"
                    dangerouslySetInnerHTML={{ __html: diffHtml || '<p class="text-muted-foreground">Computing diff…</p>' }}
                    data-testid="rewrite-ai-content-diff"
                  />
                ) : (
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none
                      [&_h2]:text-base [&_h3]:text-sm
                      [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto"
                    dangerouslySetInnerHTML={{ __html: aiDraft.content_html || '' }}
                    data-testid="rewrite-ai-content-html"
                  />
                )}
              </div>
              {showDiff && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500/40 mr-1"></span>added
                  &nbsp;&nbsp;
                  <span className="inline-block w-2 h-2 rounded-sm bg-red-500/40 mr-1"></span>removed
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row justify-between gap-2 pt-3 border-t">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="rewrite-reject-btn"
          >
            <X className="h-4 w-4 mr-1" /> Reject &amp; keep original
          </Button>
          <Button
            disabled={!aiDraft}
            onClick={handleAccept}
            className="bg-[#00A896] text-white hover:bg-[#008b7a]"
            data-testid="rewrite-accept-btn"
          >
            <Check className="h-4 w-4 mr-1" /> Edit &amp; Replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RewriteWithAiDialog;
